// Vandaag (spec 4.1): the day as a time rail. The current block is marked "nu", earlier blocks fold into one line.

import { useEffect, useRef, useState } from "react";
import { SundayReview } from "../components/WeekReview";
import { EnergyCheck } from "../components/EnergyCheck";
import { ArrowLeft, CalendarClock, Check, ChevronDown, ExternalLink, MapPin, Pencil } from "lucide-react";
import { toast } from "sonner";
import { buildTimeline, countsTowardsProgress, isCheckable, type TimelineItem } from "@shared/rhythm";
import type { Occurrence } from "@shared/recurrence";
import { openAppointment } from "../components/AppointmentSheet";
import { useOccurrences } from "../lib/appointments";
import { addDays, displayDate, displayTime, minutesToTime, planningDate, planningMinutes, timeToMinutes } from "@shared/time";
import { SyncBadge } from "../components/SyncBadge";
import { cn } from "../components/ui";
import type { DayBlock, DayTemplateBlock, PlanEntry } from "../lib/db";
import { ensureDays, setBlockStatus, useDay } from "../lib/days";
import { navigate, useNow } from "../lib/hooks";
import { isPausedIn, useRhythm } from "../lib/rhythm";
import { openDayType } from "../components/DayTypeSheet";
import type { Me } from "../lib/session";
import { Elsewhere, EntryList, NogDezeWeek, NogOpen, useDayEntries, WelcomeBack } from "./TodayTasks";
import { EveningStart, LayOutInBlock, QuickPlan } from "./EveningLinks";
import { ChorePicker } from "../components/ChorePicker";
import { checkAllFor } from "../lib/evening";
import { useCollapsed } from "../lib/hooks";
import { FoldedEntries, SoleRoutine, soleRoutine } from "./TodayTasks";
import { LateButton } from "../components/LateSheet";
import { MealLine } from "../components/MealSheet";
import { dinnerBlockId } from "@shared/meals";
import { useMeal } from "../lib/meals";
import { useMorningMode, useWindDown } from "../lib/settings";
import { useSession } from "../lib/evening";
import { DatesOfDay, ReflectionCard, RestScreen, useRestScreen } from "./TodayExtras";
import { getMeta, setMeta } from "../lib/db";
import { useLiveQuery } from "dexie-react-hooks";
import { Button } from "../components/ui";

/** Blocks after "nu" that are shown in full; the rest of the day is compact (spec V1 AC2). */
const EXPANDED_AHEAD = 2;

type AnyBlock = DayBlock | (DayTemplateBlock & { status?: undefined });

/** An appointment on the rail: at its own time, with a lock (it never moves along). */
type AppointmentItem = { kind: "appointment"; occurrence: Occurrence; start: number; end: number };
type RailItem = TimelineItem<AnyBlock> | AppointmentItem;

function mergeRail(items: TimelineItem<AnyBlock>[], occurrences: Occurrence[]): RailItem[] {
  const appointments: AppointmentItem[] = occurrences.map((o) => ({
    kind: "appointment",
    occurrence: o,
    start: timeToMinutes(o.startLocal.slice(11, 16)),
    end: o.endLocal.slice(0, 10) > o.date ? 24 * 60 : timeToMinutes(o.endLocal.slice(11, 16)),
  }));
  return [...items, ...appointments].sort((a, b) => a.start - b.start);
}

function currentRailIndex(items: RailItem[], minute: number) {
  let index = -1;
  items.forEach((item, i) => {
    if (item.start <= minute) index = i;
  });
  return index;
}

function capitalize(text: string) {
  return text.charAt(0).toUpperCase() + text.slice(1);
}

function isDone(block: AnyBlock) {
  return "status" in block && block.status === "done";
}

function isSkipped(block: AnyBlock) {
  return "status" in block && block.status === "skipped";
}

function toggle(block: DayBlock) {
  const next = block.status === "done" ? "open" : "done";
  const previous = block.status;
  void setBlockStatus(block, next);
  // Klaarleggen: the round means "all laid out" (B32): every item for tomorrow is checked along.
  if (block.slot_kind === "prep" && next === "done") void checkAllFor(addDays(block.plan_date, 1));
  if (next === "done") {
    toast(`${block.title} afgevinkt`, {
      duration: 5000,
      action: { label: "Ongedaan maken", onClick: () => void setBlockStatus(block, previous) },
    });
  }
}

/** Vandaag, or (with `date`) another day from the Week: then without "Nu", Nog open and the like. */
export function TodayScreen({ me, date: shown }: { me: Me; date?: string }) {
  const now = useNow();
  const dayStart = timeToMinutes(me.settings?.day_starts_at ?? "04:00");
  const today = planningDate(now, dayStart);
  const date = shown ?? today;
  const isToday = date === today;
  const minute = isToday ? planningMinutes(now, dayStart) : -1;
  const day = useDay(date);
  const rhythm = useRhythm();
  const [showPast, setShowPast] = useState(false);
  const currentRef = useRef<HTMLLIElement>(null);
  const scrolledFor = useRef<string | null>(null);

  useEffect(() => {
    if (isToday) void ensureDays(date);
  }, [date, isToday]);

  const meal = useMeal(date);
  const rawBlocks: AnyBlock[] = day.kind === "plan" ? day.blocks : day.kind === "preview" ? day.blocks : [];
  const dinnerId = dinnerBlockId(rawBlocks);
  // "Hoe was je dag?" (golf 9) belongs with the last rest block of the day (Wind down).
  const lastRestId = [...rawBlocks]
    .filter((b) => b.slot_kind === "rest")
    .sort((a, b) => (a.start_local < b.start_local ? -1 : 1))
    .pop()?.id;
  // "Uit eten" turns the cooking block into "Uit eten" (E1 AC3); only the meal says so, the block stays as it is.
  const blocks = meal?.place === "out" ? rawBlocks.map((b) => (b.id === dinnerId ? { ...b, title: "Uit eten" } : b)) : rawBlocks;
  const occurrences = useOccurrences(date, date);
  const items = mergeRail(buildTimeline(blocks), occurrences ?? []);
  const current = currentRailIndex(items, minute);
  // Fixed blocks (work, bedtime): an appointment that overlaps one gets a warning; nothing moves (A1 AC5).
  const fixed = items.filter((i): i is Extract<RailItem, { kind: "block" }> => i.kind === "block" && i.block.is_fixed);
  const past = current > 0 ? items.slice(0, current) : [];
  const upcoming = current >= 0 ? items.slice(current) : items;
  const checkable = day.kind === "plan" && isToday;
  const dayType = day.kind === "plan" ? day.plan.template_name : day.kind === "preview" ? day.templateName : null;
  // A preview (day not yet on the device) is read only: no checklist, no planning links (tester 5a B2).
  const actionable = isToday && day.kind !== "preview";
  const extraFor = (block: AnyBlock) => {
    if (block.id === dinnerId) return <MealLine date={date} editable={date >= today} />;
    if (!actionable && !(block.slot_kind === "flex" && date > today && "status" in block && block.status)) return null;
    if (block.slot_kind === "planning") return <EveningStart today={date} />;
    if (block.id === lastRestId) return <ReflectionCard date={date} />;
    if (block.slot_kind === "prep")
      return <LayOutInBlock today={date} block={"status" in block && block.status ? (block as DayBlock) : undefined} />;
    // Klusje kiezen (B32): a task from the list straight into this block.
    if (block.slot_kind === "flex" && "status" in block && block.status) return <ChorePicker date={date} block={block as DayBlock} />;
    return null;
  };
  const morningMode = useMorningMode();
  const morningDismissed = useLiveQuery(async () => (await getMeta<string>("morningDismissed")) === date, [date]);
  const morning = day.kind === "plan" && actionable && morningMode && morningDismissed === false ? morningStep(day.blocks, minute) : null;
  // Ziek or Vakantie: appointments stay, with the question whether to cancel (R3 AC4).
  const paused = isPausedIn(rhythm)(date);
  const entries = useDayEntries(date, new Set(day.kind === "plan" ? day.blocks.map((b) => b.id) : []));
  // Wind down (B49): after bedtime Vandaag rests, softly.
  const windDown = useWindDown();
  const resting = useRestScreen(date, actionable ? minute : -1, day.kind === "plan" ? day.blocks : null, windDown);
  const tomorrowSession = useSession(addDays(date, 1), "evening");

  // Open on the current block, once per day (spec V1 AC1).
  useEffect(() => {
    if (day.kind === "loading" || scrolledFor.current === date) return;
    scrolledFor.current = date;
    currentRef.current?.scrollIntoView({ block: "center" });
  }, [day.kind, date]);

  const pastBlocks = past.flatMap((i) => (i.kind === "group" ? i.blocks : i.kind === "block" ? [i.block] : []));
  const pastCounted = pastBlocks.filter((b) => countsTowardsProgress({ ...b, status: "status" in b && b.status ? b.status : "open" }));
  const pastDone = pastCounted.filter(isDone).length;

  // Before the setting is read, a morning shows nothing rather than the whole day for a moment (review 5b).
  const morningCandidate = day.kind === "plan" && actionable ? morningStep(day.blocks, minute) : null;
  if (morningCandidate && (morningMode === undefined || morningDismissed === undefined)) return <main aria-busy className="min-h-dvh" />;
  if (resting === undefined && actionable && day.kind === "plan") return <main aria-busy className="min-h-dvh" />;
  if (resting) return <RestScreen date={date} tomorrowReady={!!tomorrowSession?.finished_at} />;
  if (morning)
    return (
      <MorningView date={date} step={morning.step} left={morning.left} onWholeDay={() => void setMeta("morningDismissed", date)}>
        <QuickPlan today={date} minute={minute} />
      </MorningView>
    );

  return (
    <main className="mx-auto max-w-xl px-4 pt-[max(1.25rem,env(safe-area-inset-top))] pb-44">
      {!isToday && (
        <a
          href="/week"
          onClick={(e) => {
            e.preventDefault();
            navigate("/week");
          }}
          className="-ml-2 mb-2 inline-flex min-h-11 items-center gap-1 rounded-xl px-2 text-pine"
        >
          <ArrowLeft aria-hidden className="size-5" />
          Week
        </a>
      )}
      <header className="mb-6 flex flex-wrap items-end justify-between gap-x-4 gap-y-2">
        <div>
          <h1 className="text-[1.75rem] leading-tight font-bold">{capitalize(displayDate(date))}</h1>
          {dayType && (
            <button
              type="button"
              onClick={() => openDayType(date)}
              aria-label={`Dagtype ${dayType}, wijzigen`}
              className="mt-1 -ml-1 inline-flex min-h-11 items-center gap-1.5 rounded-xl px-1 text-ink-soft"
            >
              {/* L37: the type only shows in words when it differs from the weekday; the button is always there (V1 AC3). */}
              <span>{dayType.toLowerCase() !== displayDate(date).split(" ")[0] ? dayType : "Dagtype"}</span>
              <Pencil aria-hidden className="size-4" />
            </button>
          )}
        </div>
        <SyncBadge />
      </header>

      {day.kind === "preview" && (
        <p role="note" className="mb-5 rounded-2xl border border-mist bg-surface p-4 text-ink-soft">
          {isToday
            ? "Je bent offline. Dit is je ritme voor vandaag; afvinken kan zodra je dag is bijgewerkt."
            : "Deze dag staat nog niet klaar. Dit is je ritme; routines komen erbij als de dag dichterbij is."}
        </p>
      )}
      {isToday && <WelcomeBack date={date} />}
      <DatesOfDay date={date} />
      {actionable && <QuickPlan today={date} minute={minute} />}
      {/* De energiecheck (AI golf C), in the morning until it is answered; after Moe it stays for the proposal. */}
      {actionable && day.kind === "plan" && minute < 12 * 60 && (!day.plan.energy || day.plan.energy === "moe") && (
        <EnergyCheck date={date} />
      )}
      {isToday && <NogOpen date={date} />}
      <Elsewhere entries={entries.get("") ?? []} />
      {actionable && <SundayReview date={date} />}
      {isToday && <NogDezeWeek date={date} />}
      {actionable && day.kind === "plan" && (
        <LateButton
          date={date}
          blocks={day.blocks}
          appointments={(occurrences ?? []).map((o) => ({ title: o.title, start: timeToMinutes(o.startLocal.slice(11, 16)) }))}
          minute={minute}
        />
      )}

      {day.kind !== "loading" && !items.length && (
        <p className="rounded-2xl border border-mist bg-surface p-4 text-ink-soft">
          Er staat niets in je dagritme voor {isToday ? "vandaag" : "deze dag"}.
        </p>
      )}

      {past.length > 0 && (
        <div className="mb-2">
          <button
            type="button"
            aria-expanded={showPast}
            onClick={() => setShowPast((v) => !v)}
            className="flex min-h-12 w-full items-center justify-between rounded-2xl px-3 text-left text-ink-soft hover:bg-surface"
          >
            <span>Eerder vandaag</span>
            <span className="flex items-center gap-2 tabular">
              {pastCounted.length > 0 && `${pastDone} van ${pastCounted.length} klaar`}
              <ChevronDown aria-hidden className={cn("size-5 transition-transform", showPast && "rotate-180")} />
            </span>
          </button>
          {showPast && (
            <ol className="mt-1">
              {past.map((item) => (
                <TimelineRow
                  key={itemKey(item)}
                  item={item}
                  variant="compact"
                  checkable={checkable}
                  entries={entries}
                  fixed={fixed}
                  paused={paused}
                  extraFor={extraFor}
                />
              ))}
            </ol>
          )}
        </div>
      )}

      <ol>
        {upcoming.map((item, i) => (
          <TimelineRow
            key={itemKey(item)}
            ref={i === 0 && current >= 0 ? currentRef : undefined}
            item={item}
            variant={i === 0 && current >= 0 ? "now" : i <= EXPANDED_AHEAD || !isToday ? "full" : "compact"}
            paused={paused}
            extraFor={extraFor}
            nowLabel={i === 0 && current >= 0 ? minutesToTime(minute) : undefined}
            checkable={checkable}
            entries={entries}
            fixed={fixed}
          />
        ))}
      </ol>
    </main>
  );
}

/**
 * Ochtendstand (V6): from the start of the day until the end of the first group (the morning steps), as long as a
 * step is still open. Which group that is comes from the day type, not from a name in the code.
 */
function morningStep(blocks: DayBlock[], minute: number): { step: DayBlock; left: number } | null {
  const items = buildTimeline(blocks);
  const first = items.find((i) => i.kind === "group");
  if (!first || first.kind !== "group" || first.start >= 12 * 60 || minute >= first.end) return null;
  const open = first.blocks.filter((b) => b.status === "open");
  return open[0] ? { step: open[0], left: open.length } : null;
}

/** Snel plannen (AV1 AC6) stays in sight in the morning: it is exactly the moment it is for. */
function MorningView({
  date,
  step,
  left,
  onWholeDay,
  children,
}: {
  date: string;
  step: DayBlock;
  left: number;
  onWholeDay: () => void;
  children?: React.ReactNode;
}) {
  return (
    <main className="mx-auto flex min-h-dvh max-w-xl flex-col px-4 pt-[max(1.25rem,env(safe-area-inset-top))] pb-44">
      <header className="mb-10 flex items-center justify-between gap-3">
        <p className="text-ink-soft">{capitalize(displayDate(date))}</p>
        <SyncBadge />
      </header>
      {children}
      <EnergyCheck date={date} />
      <section aria-label="Ochtendstand" className="flex flex-1 flex-col justify-center">
        <p className="mb-2 text-ink-soft">{left === 1 ? "Nog 1 stap" : `Nog ${left} stappen`}</p>
        <h1 className="mb-2 text-[2.75rem] leading-tight font-bold [overflow-wrap:anywhere]">{step.title}</h1>
        <p className="mb-10 text-lg text-ink-soft tabular">{displayTime(step.start_local)}</p>
        <Button className="min-h-16 w-full text-xl" onClick={() => toggle(step)}>
          <Check aria-hidden className="size-6" strokeWidth={3} />
          Klaar
        </Button>
        <button
          type="button"
          onClick={onWholeDay}
          className="mx-auto mt-6 min-h-11 rounded-xl px-3 text-ink-soft underline underline-offset-4"
        >
          Hele dag tonen
        </button>
      </section>
    </main>
  );
}

function itemKey(item: RailItem) {
  if (item.kind === "appointment") return `a-${item.occurrence.appointmentId}-${item.occurrence.recurrenceLocal}`;
  return item.kind === "group" ? `g-${item.blocks[0]!.id}` : item.block.id;
}

type Variant = "now" | "full" | "compact";

function TimelineRow({
  item,
  variant,
  nowLabel,
  checkable,
  entries,
  fixed,
  paused = false,
  extraFor,
  ref,
}: {
  item: RailItem;
  variant: Variant;
  nowLabel?: string;
  checkable: boolean;
  entries: Map<string, PlanEntry[]>;
  fixed: Extract<RailItem, { kind: "block" }>[];
  paused?: boolean;
  /** Something of its own under a block: the evening planning in "Volgende dag plannen", the checklist in "Klaarleggen". */
  extraFor?: (block: AnyBlock) => React.ReactNode;
  ref?: React.Ref<HTMLLIElement>;
}) {
  const startTime =
    item.kind === "appointment"
      ? item.occurrence.startLocal.slice(11, 19)
      : (item.kind === "group" ? item.blocks[0]! : item.block).start_local;
  const now = variant === "now";
  return (
    <li ref={ref} className="grid grid-cols-[3rem_1fr] gap-x-2">
      <div className={cn("pt-3.5 text-right text-sm tabular", now ? "font-bold text-ink" : "text-ink-soft")}>{displayTime(startTime)}</div>
      <div className={cn("relative min-w-0 border-l-2 pb-3 pl-3", now ? "border-sun" : "border-mist")}>
        <span
          aria-hidden
          className={cn(
            "absolute top-[1.15rem] -left-[7px] size-3 rounded-full border-2",
            now ? "size-4 -left-[9px] border-sun bg-sun" : "border-mist bg-paper",
          )}
        />
        {now && nowLabel && (
          <p className="mb-1 pt-2 text-sm font-semibold text-ink">
            <span className="rounded-md bg-sun px-1.5 py-0.5 text-sun-ink">Nu</span> <span className="tabular">{nowLabel}</span>
          </p>
        )}
        {item.kind === "appointment" ? (
          <AppointmentLine
            item={item}
            variant={variant}
            clash={fixed.find((f) => item.start < f.end && item.end > f.start)?.block.title}
            paused={paused}
          />
        ) : item.kind === "group" ? (
          <>
            <GroupCard item={item} variant={variant} checkable={checkable} extraFor={extraFor} />
            <EntryList entries={item.blocks.flatMap((b) => entries.get(b.id) ?? [])} large={now} />
          </>
        ) : (
          <>
            {soleRoutine(item.block, entries.get(item.block.id) ?? []) ? (
              // Sport with only the gym in it: one line, not Sport and Gym twice (B32).
              <SoleRoutine block={item.block} entry={(entries.get(item.block.id) ?? [])[0]!} variant={variant} checkable={checkable} />
            ) : (
              <>
                <BlockLine
                  block={item.block}
                  variant={variant}
                  checkable={checkable}
                  end={item.end}
                  empty={!(entries.get(item.block.id) ?? []).length}
                />
                <FoldedEntries id={item.block.id} entries={entries.get(item.block.id) ?? []} large={now} />
              </>
            )}
            {extraFor?.(item.block)}
          </>
        )}
      </div>
    </li>
  );
}

/** A block's round; `small` for a step inside a group, so the group and its steps read as a whole and its parts. */
function CheckCircle({ done, small = false }: { done: boolean; small?: boolean }) {
  return (
    <span
      aria-hidden
      className={cn(
        "flex shrink-0 items-center justify-center rounded-full border-2",
        small ? "size-6" : "size-7",
        done ? "border-pine bg-pine text-paper" : "border-ink-soft",
      )}
    >
      {done && <Check className="size-4" strokeWidth={3} />}
    </span>
  );
}

function BlockLine({
  block,
  variant,
  checkable,
  end,
  empty = false,
}: {
  block: AnyBlock;
  variant: Variant;
  checkable: boolean;
  end: number;
  empty?: boolean;
}) {
  const done = isDone(block);
  const size = variant === "now" ? "text-xl font-bold" : variant === "full" ? "text-lg font-semibold" : "text-base";
  const range = `${displayTime(block.start_local)} tot ${displayTime(minutesToTime(end))}`;

  if (!isCheckable(block)) {
    return (
      <div className={cn("rounded-2xl py-2", variant !== "compact" && "bg-surface px-3")}>
        <p className={cn(size, "[overflow-wrap:anywhere]")}>{block.title}</p>
        <p className="text-sm text-ink-soft tabular">{range}</p>
        {block.external_url && (
          <a
            href={block.external_url}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-2 inline-flex min-h-11 items-center gap-1.5 rounded-xl border-2 border-mist px-3 font-semibold text-pine"
          >
            Open werkbank
            <ExternalLink aria-hidden className="size-4" />
          </a>
        )}
      </div>
    );
  }

  // An optional block with nothing in it is room, not a task: no round to check, and quieter than what has something in it.
  if (block.is_optional && empty && !done && !isSkipped(block)) {
    return (
      <div className="flex min-h-11 items-center gap-3 px-2 py-1 text-ink-soft">
        <span className="min-w-0 flex-1 [overflow-wrap:anywhere]">{block.title}</span>
        <span className="text-sm">optioneel</span>
      </div>
    );
  }

  const label = (
    <>
      <span
        className={cn("min-w-0 flex-1 [overflow-wrap:anywhere]", size, (done || isSkipped(block)) && "text-done line-through decoration-2")}
      >
        {block.title}
      </span>
      {isSkipped(block) ? (
        <span className="text-sm text-ink-soft">overgeslagen</span>
      ) : (
        block.is_optional && !done && <span className="text-sm text-ink-soft">optioneel</span>
      )}
    </>
  );

  if (!checkable || !("status" in block) || !block.status) {
    return <div className="flex min-h-12 items-center gap-3 py-1">{label}</div>;
  }

  const real = block as DayBlock;
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={done}
      onClick={() => toggle(real)}
      className={cn(
        "flex min-h-12 w-full items-center gap-3 rounded-2xl px-2 py-1 text-left",
        variant === "now" && "bg-surface px-3 py-3",
        // Optional stays optional, also with something in it (B32): dashed, and it never counts as missed.
        block.is_optional && !done && "border-2 border-dashed border-mist",
      )}
    >
      <CheckCircle done={done} />
      {label}
    </button>
  );
}

function GroupCard({
  item,
  variant,
  checkable,
  extraFor,
}: {
  item: Extract<TimelineItem<AnyBlock>, { kind: "group" }>;
  variant: Variant;
  checkable: boolean;
  extraFor?: (block: AnyBlock) => React.ReactNode;
}) {
  const done = item.blocks.filter(isDone).length;
  // A skipped step (Ik loop achter, L45) no longer counts.
  const total = item.blocks.filter((b) => !isSkipped(b)).length;
  const compact = variant === "compact";
  // In and out folding (B32): folded, the count stays in sight.
  const [folded, toggleFold] = useCollapsed(`${"plan_date" in item.blocks[0]! ? item.blocks[0].plan_date : ""}:${item.title}`);
  return (
    <section aria-label={item.title} className={cn("rounded-2xl", !compact && "bg-surface p-3")}>
      <button
        type="button"
        aria-expanded={!folded}
        onClick={toggleFold}
        className="flex min-h-11 w-full items-center justify-between gap-3 text-left"
      >
        <h2 className={cn(variant === "now" ? "text-xl font-bold" : variant === "full" ? "text-lg font-semibold" : "text-base")}>
          {item.title}
        </h2>
        <span className="flex items-center gap-2">
          <span className={cn("text-sm tabular", done === total ? "font-semibold text-pine" : "text-ink-soft")}>
            {done === total ? "Klaar" : `${done} van ${total}`}
          </span>
          <ChevronDown aria-hidden className={cn("size-5 text-ink-soft transition-transform", !folded && "rotate-180")} />
        </span>
      </button>
      {/* No second line next to the rail (PK: two timelines took the room); the smaller rounds set the steps apart. */}
      {!folded && (
        <ul className="mt-1">
          {item.blocks.map((block) => {
            const isReal = checkable && "status" in block && !!block.status;
            const stepDone = isDone(block);
            const content = (
              <>
                {isReal && <CheckCircle done={stepDone} small />}
                <span
                  className={cn(
                    "min-w-0 flex-1 [overflow-wrap:anywhere]",
                    (stepDone || isSkipped(block)) && "text-done line-through decoration-2",
                  )}
                >
                  {block.title}
                </span>
                {isSkipped(block) && <span className="text-sm text-ink-soft">overgeslagen</span>}
                <span className="text-sm text-ink-soft tabular">{displayTime(block.start_local)}</span>
              </>
            );
            return (
              <li key={block.id}>
                {isReal ? (
                  <button
                    type="button"
                    role="checkbox"
                    aria-checked={stepDone}
                    onClick={() => toggle(block as DayBlock)}
                    className="flex min-h-12 w-full items-center gap-3 rounded-xl px-1 text-left"
                  >
                    {content}
                  </button>
                ) : (
                  <div className="flex min-h-12 items-center gap-3 px-1">{content}</div>
                )}
                {extraFor?.(block)}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

function AppointmentLine({ item, variant, clash, paused }: { item: AppointmentItem; variant: Variant; clash?: string; paused: boolean }) {
  const o = item.occurrence;
  const size = variant === "now" ? "text-xl font-bold" : variant === "full" ? "text-lg font-semibold" : "text-base font-semibold";
  return (
    // An appointment looks like no task (PK): a tinted card with a bar, a calendar and the word Afspraak, no round to check.
    <button
      type="button"
      onClick={() => openAppointment({ kind: "occurrence", occurrence: o })}
      className="flex w-full flex-col gap-0.5 rounded-2xl border-l-[6px] border-pine bg-pine/15 px-3 py-2 text-left"
    >
      <span className="flex items-center gap-1.5 text-sm font-semibold text-pine">
        <CalendarClock aria-label="Vaste afspraak" className="size-4 shrink-0" />
        <span aria-hidden>Afspraak</span>
      </span>
      <span className={cn("min-w-0 [overflow-wrap:anywhere]", size)}>{o.title}</span>
      <span className="text-sm text-ink-soft tabular">
        {displayTime(o.startLocal.slice(11, 16))} tot {displayTime(o.endLocal.slice(11, 16))}
      </span>
      {o.travelMinutes > 0 && (
        <span className="text-sm font-semibold text-pine tabular">
          Vertrek {displayTime(minutesToTime((((timeToMinutes(o.startLocal.slice(11, 16)) - o.travelMinutes) % 1440) + 1440) % 1440))}
        </span>
      )}
      {o.location && (
        <span className="inline-flex min-w-0 items-center gap-1 text-sm text-ink-soft [overflow-wrap:anywhere]">
          <MapPin aria-hidden className="size-3.5" />
          {o.location}
        </span>
      )}
      {clash && <span className="text-sm font-semibold text-ink">Valt samen met {clash}</span>}
      {paused && <span className="text-sm font-semibold text-ink">Afzeggen? Tik om hem over te slaan of te verzetten.</span>}
    </button>
  );
}
